import React, { useState, useEffect } from "react";
import { toast } from 'react-toastify';
import axios from "axios";
import { useAuth } from '../../context/AuthContext';
import HistorialPagosCuentaCorriente from './HistorialPagosCuentaCorriente';

const CobroCuentaCorriente = ({ cliente, onCobroCuenta, onClose, caja }) => {
  const { token, userId } = useAuth();
  const [monto, setMonto] = useState("");
  const [formaPago, setFormaPago] = useState("efectivo");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resumenDeuda, setResumenDeuda] = useState(null);
  const [loadingResumen, setLoadingResumen] = useState(true);
  const [mostrarDistribucion, setMostrarDistribucion] = useState(false);
  const [mostrarHistorial, setMostrarHistorial] = useState(false);

  // Obtener resumen completo de deuda del cliente
  useEffect(() => {
    const fetchResumenDeuda = async () => {
      try {
        setLoadingResumen(true);
        const response = await axios.get(
          `${import.meta.env.VITE_API_URL}/cuentas-corriente/cliente/${cliente.id}/resumen`,
          { headers: { 'x-auth-token': token } }
        );
        setResumenDeuda(response.data);
      } catch (err) {
        console.error('Error al obtener resumen de deuda:', err);
        toast.error('Error al cargar el resumen de deuda');
      } finally {
        setLoadingResumen(false);
      }
    };

    if (cliente?.id) {
      fetchResumenDeuda();
    }
  }, [cliente?.id, token]);

  // Calcular distribución del pago
  const calcularDistribucion = () => {
    if (!resumenDeuda || !monto) return [];
    
    const montoNum = parseFloat(monto);
    if (isNaN(montoNum) || montoNum <= 0) return [];

    let montoRestante = montoNum;
    const distribucion = [];

    // Ordenar cuentas por fecha (FIFO - más antiguas primero)
    const cuentasOrdenadas = [...resumenDeuda.cuentas_pendientes].sort((a, b) => 
      new Date(a.fecha_venta) - new Date(b.fecha_venta)
    );

    for (const cuenta of cuentasOrdenadas) {
      if (montoRestante <= 0) break;
      
      const saldoPendiente = parseFloat(cuenta.saldo_pendiente);
      const montoParaEsta = Math.min(montoRestante, saldoPendiente);
      
      if (montoParaEsta > 0) {
        distribucion.push({
          cuenta_id: cuenta.id,
          id_venta: cuenta.id_venta,
          descripcion: cuenta.descripcion,
          saldo_actual: saldoPendiente,
          monto_aplicado: montoParaEsta,
          saldo_restante: saldoPendiente - montoParaEsta,
          fecha_venta: cuenta.fecha_venta,
          liquidada_completamente: montoParaEsta === saldoPendiente
        });
        
        montoRestante -= montoParaEsta;
      }
    }

    return distribucion;
  };

  const handleCobro = async () => {
    if (!caja) {
      toast.error("Debe abrir una caja para cobrar cuentas corrientes");
      return;
    }
    
    const montoNum = parseFloat(monto);
    if (!montoNum || montoNum <= 0) {
      setError("Ingrese un monto válido");
      return;
    }
    
    if (!resumenDeuda) {
      setError("Error al cargar información de deuda");
      return;
    }
    
    const totalDeuda = parseFloat(resumenDeuda.total_deuda);
    if (montoNum > totalDeuda) {
      setError(`El monto no puede ser mayor a la deuda total ($${totalDeuda.toFixed(2)})`);
      return;
    }
    
    setError("");
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/cuentas-corriente/cobro`,
        { 
          cliente_id: cliente.id,
          monto: montoNum, 
          forma_pago: formaPago, 
          caja_id: parseInt(caja), 
          userId 
        },
        { headers: { 'x-auth-token': token } }
      );

      // Mostrar éxito con detalles de la distribución
      const { distribucion, resumen } = response.data;
      const cuentasAfectadas = distribucion ? distribucion.length : 0;
      
      toast.success(
        `Pago procesado exitosamente. Se aplicó $${montoNum.toFixed(2)} a ${cuentasAfectadas} cuenta(s)`,
        { autoClose: 5000 }
      );

      // Actualizar el resumen de deuda
      await fetchResumenDeuda();
      
      // Limpiar formulario
      setMonto("");
      setMostrarDistribucion(false);
      
      // Notificar al componente padre
      if (onCobroCuenta) {
        onCobroCuenta(montoNum, formaPago, response.data);
      }
      
    } catch (err) {
      console.error('Error al procesar cobro:', err);
      const errorMsg = err.response?.data?.error || 'Error al procesar el cobro';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Función auxiliar para refrescar el resumen
  const fetchResumenDeuda = async () => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/cuentas-corriente/cliente/${cliente.id}/resumen`,
        { headers: { 'x-auth-token': token } }
      );
      setResumenDeuda(response.data);
    } catch (err) {
      console.error('Error al actualizar resumen:', err);
    }
  };

  // Si se está mostrando el historial, renderizar solo ese componente
  if (mostrarHistorial) {
    return (
      <HistorialPagosCuentaCorriente 
        cliente={cliente}
        onClose={() => setMostrarHistorial(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-3">
      <div className="max-w-7xl mx-auto">
        {/* Header compacto */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4 border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-2 rounded-lg">
                <span className="text-white text-lg">💰</span>
              </div>
              <div>
                <h4 className="text-lg font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                  Cobrar Cuenta Corriente
                </h4>
                <p className="text-sm text-gray-600">Gestiona los pagos de manera eficiente</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-all duration-200"
            >
              <span className="text-xl">×</span>
            </button>
          </div>
        </div>

        {/* Información del cliente compacta */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4 border border-gray-100">
          <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-r from-blue-500 to-indigo-500 p-2 rounded-lg">
                <span className="text-white text-lg">👤</span>
              </div>
              <div>
                <h5 className="text-lg font-bold text-gray-800">{cliente.nombre}</h5>
                <p className="text-sm text-gray-600">Cliente</p>
              </div>
            </div>
            <button
              onClick={() => setMostrarHistorial(true)}
              className="px-3 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 flex items-center gap-2 text-sm"
            >
              <span>📋</span>
              Ver Historial
            </button>
          </div>
          
          {loadingResumen ? (
            <div className="animate-pulse space-y-2">
              <div className="flex justify-between items-center">
                <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                <div className="h-3 bg-gray-200 rounded w-1/4"></div>
              </div>
              <div className="flex justify-between items-center">
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/5"></div>
              </div>
            </div>
          ) : resumenDeuda ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-3">
              <div className="bg-gradient-to-r from-red-50 to-red-100 p-3 rounded-lg border border-red-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-red-600 mb-1">Total de Deuda</p>
                    <p className="text-lg font-bold text-red-700">${parseFloat(resumenDeuda.total_deuda).toFixed(2)}</p>
                  </div>
                  <div className="bg-red-500 p-2 rounded-full">
                    <span className="text-white text-sm">⚠️</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-blue-600 mb-1">Cuentas Pendientes</p>
                    <p className="text-lg font-bold text-blue-700">{resumenDeuda.cantidad_cuentas}</p>
                  </div>
              
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 flex items-center gap-2">
              <span className="text-lg">❌</span>
              <span className="text-sm">Error al cargar información de deuda</span>
            </div>
          )}
          
          <div className="mt-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-xs text-blue-700 flex items-center gap-2">
              <span className="text-sm">💡</span>
              <span><strong>Tip:</strong> Los pagos se aplican automáticamente a las deudas más antiguas primero (FIFO)</span>
            </p>
          </div>
        </div>

        {/* Formulario de pago compacto */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4 border border-gray-100">
          <div className="flex items-center gap-2 mb-4">
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-2 rounded-lg">
              <span className="text-white text-lg">💳</span>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">Procesar Pago</h3>
              <p className="text-sm text-gray-600">Ingresa los detalles del pago</p>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="xl:col-span-2">
              <label className="block text-sm font-semibold mb-2 text-gray-700">Forma de Pago</label>
              <div className="grid grid-cols-2 xl:grid-cols-4 gap-2">
                {[
                  { value: 'efectivo', label: 'Efectivo', icon: '💵', color: 'from-green-500 to-green-600' },
                  { value: 'transferencia', label: 'Transferencia', icon: '🏦', color: 'from-blue-500 to-blue-600' },
                  { value: 'tarjeta', label: 'Tarjeta', icon: '💳', color: 'from-purple-500 to-purple-600' },
                  { value: 'otros', label: 'Otros', icon: '�️', color: 'from-orange-500 to-orange-600' }
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`cursor-pointer transition-all duration-200 ${
                      formaPago === option.value ? 'transform scale-105' : 'hover:scale-102'
                    }`}
                  >
                    <div
                      className={`p-2 rounded-lg border-2 transition-all duration-200 ${
                        formaPago === option.value
                          ? `bg-gradient-to-r ${option.color} text-white border-transparent shadow-lg`
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-center gap-1">
                        <input
                          type="radio"
                          value={option.value}
                          checked={formaPago === option.value}
                          onChange={(e) => setFormaPago(e.target.value)}
                          className="sr-only"
                        />
                        <span className="text-sm">{option.icon}</span>
                        <span className="font-medium text-xs">{option.label}</span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">Monto a Cobrar</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg font-semibold">$</span>
                <input
                  type="number"
                  className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:ring focus:ring-green-200 transition-all duration-200 text-lg font-semibold"
                  placeholder="0.00"
                  value={monto}
                  onChange={e => {
                    setMonto(e.target.value);
                    setMostrarDistribucion(false);
                  }}
                  min="0.01"
                  max={resumenDeuda?.total_deuda ? parseFloat(resumenDeuda.total_deuda) : 0}
                  step="0.01"
                />
              </div>
              {resumenDeuda && (
                <div className="mt-1 flex items-center gap-2 text-xs text-gray-600">
                  <span className="bg-gray-100 px-2 py-1 rounded-lg">
                    Máximo: ${parseFloat(resumenDeuda.total_deuda).toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Vista previa de distribución compacta */}
        {monto && parseFloat(monto) > 0 && resumenDeuda && (
          <div className="bg-white rounded-xl shadow-lg p-4 mb-4 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="bg-gradient-to-r from-yellow-500 to-orange-500 p-2 rounded-lg">
                  <span className="text-white text-lg">👁️</span>
                </div>
                <div>
                  <h6 className="text-lg font-bold text-gray-800">Vista Previa del Pago</h6>
                  <p className="text-sm text-gray-600">Cómo se distribuirá tu pago</p>
                </div>
              </div>
              <button
                onClick={() => setMostrarDistribucion(!mostrarDistribucion)}
                className="px-3 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg hover:from-yellow-600 hover:to-orange-600 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 text-sm"
              >
                {mostrarDistribucion ? '🙈 Ocultar' : '👁️ Mostrar'} Distribución
              </button>
            </div>
            
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 rounded-lg border border-blue-200 mb-3">
              <div className="flex items-start gap-2">
                <span className="text-lg">💡</span>
                <div>
                  <h6 className="font-semibold text-blue-800 mb-1 text-sm">¿Cómo funciona?</h6>
                  <p className="text-xs text-blue-700">
                    Tu pago de <span className="font-bold">${parseFloat(monto).toFixed(2)}</span> se aplicará automáticamente 
                    comenzando por las deudas más antiguas. Puedes pagar parcialmente cualquier deuda.
                  </p>
                </div>
              </div>
            </div>
            
            {mostrarDistribucion && (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {calcularDistribucion().map((dist, index) => (
                  <div key={dist.cuenta_id} className="bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-lg p-3 hover:shadow-md transition-all duration-200">
                    <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm">🧾</span>
                          <span className="font-bold text-gray-800 text-sm">Venta #{dist.id_venta}</span>
                          {dist.liquidada_completamente && (
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-bold flex items-center gap-1">
                              ✅ LIQUIDADA
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-600 mb-1">
                          📅 {dist.fecha_venta ? new Date(dist.fecha_venta).toLocaleDateString('es-AR') : 'Sin fecha'}
                        </div>
                        {dist.descripcion && (
                          <div className="text-xs text-gray-500 bg-gray-100 p-2 rounded-lg">
                            💬 {dist.descripcion}
                          </div>
                        )}
                      </div>
                      <div className="bg-white p-3 rounded-lg border border-gray-200 min-w-[180px]">
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Deuda actual:</span>
                            <span className="font-semibold text-red-600">${dist.saldo_actual.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Se pagará:</span>
                            <span className="font-bold text-green-600">${dist.monto_aplicado.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between items-center border-t pt-1">
                            <span className="text-gray-600">Quedará:</span>
                            <span className={`font-bold ${dist.saldo_restante === 0 ? 'text-green-600' : 'text-orange-600'}`}>
                              ${dist.saldo_restante.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="mt-4 bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-lg border border-green-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-green-800 text-sm">💰 Total a aplicar:</span>
                  <span className="text-lg font-bold text-green-700">${parseFloat(monto || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-green-800 text-sm">📊 Cuentas afectadas:</span>
                  <span className="text-lg font-bold text-green-700">{calcularDistribucion().length}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-lg p-3 mb-4 text-red-700 flex items-center gap-2">
            <span className="text-lg">❌</span>
            <span className="font-medium text-sm">{error}</span>
          </div>
        )}

        {/* Botones de acción compactos */}
        <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 disabled:from-gray-400 disabled:to-gray-500 text-white px-4 py-3 rounded-lg font-bold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none flex items-center justify-center gap-2 text-sm"
              onClick={handleCobro}
              disabled={loading || !monto || parseFloat(monto) <= 0 || !resumenDeuda}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Procesando Pago...
                </>
              ) : (
                <>
                  <span className="text-lg">💳</span>
                  Procesar Pago
                </>
              )}
            </button>
            <button
              className="px-4 py-3 bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 text-white rounded-lg font-bold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none flex items-center justify-center gap-2 text-sm"
              onClick={onClose}
              disabled={loading}
            >
              <span className="text-lg">❌</span>
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CobroCuentaCorriente;
